import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, List, ListItem, ListItemText, Divider, CircularProgress, Button, Dialog, DialogContent, IconButton, useMediaQuery, useTheme, Grid, Paper, Tooltip, Chip } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import GetAppIcon from '@mui/icons-material/GetApp';
import ShareIcon from '@mui/icons-material/Share';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import RedditIcon from '@mui/icons-material/Reddit';
import PinterestIcon from '@mui/icons-material/Pinterest';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import { AuthContext } from '../components/Auth';
import { useParams, useNavigate } from 'react-router-dom';

const formatTimestamp = (ts) => {
  if (!ts) return '';
  // Firestore Timestamp may be an object with seconds
  if (ts.toDate && typeof ts.toDate === 'function') return ts.toDate().toLocaleString();
  if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleString();
  try { return new Date(ts).toLocaleString(); } catch (e) { return String(ts); }
}

const RecipeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { firestoreInstance } = useContext(AuthContext);
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openImage, setOpenImage] = useState(false);

  useEffect(() => {
    let unsubscribe = null;
    setError(null);
    if (!firestoreInstance || !id) {
      setRecipe(null);
      setLoading(false);
      return () => {};
    }

    setLoading(true);
    import('firebase/firestore').then(({ doc, onSnapshot }) => {
      try {
        const ref = doc(firestoreInstance, 'sharedRecipes', id);
        unsubscribe = onSnapshot(ref, (snap) => {
          if (snap.exists()) {
            setRecipe({ id: snap.id, ...snap.data() });
            setError(null);
          } else {
            setRecipe(null);
            setError('Recipe not found');
          }
          setLoading(false);
        }, (err) => {
          setError(err.message || String(err));
          setLoading(false);
        });
      } catch (err) {
        setError(err.message || String(err));
        setLoading(false);
      }
    }).catch((err) => {
      setError(err.message || String(err));
      setLoading(false);
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [firestoreInstance, id]);

  if (loading) return (<Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>);

  if (error) return (
    <Box sx={{ p: 2 }}>
      <Typography color="error">{error}</Typography>
      <Button variant="outlined" sx={{ mt: 2 }} onClick={() => navigate(-1)}>Back</Button>
    </Box>
  );

  if (!recipe) return (
    <Box sx={{ p: 2 }}>
      <Typography>No recipe found.</Typography>
      <Button variant="outlined" sx={{ mt: 2 }} onClick={() => navigate(-1)}>Back</Button>
    </Box>
  );

  const ingredients = recipe.ingredients || [];
  const steps = (recipe.steps || []).slice().sort((a,b) => (a.step_number ?? 0) - (b.step_number ?? 0));

  // share helpers
  const pageUrl = (typeof window !== 'undefined' && window.location) ? window.location.href : '';
  const title = recipe.title || '';
  const openShareWindow = (url) => {
    try { window.open(url, '_blank', 'noopener,noreferrer'); } catch (e) { /* ignore */ }
  };

  const handleWebShare = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title, text: recipe.description || title, url: pageUrl });
      } catch (e) { /* user cancelled or not supported */ }
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Button variant="text" onClick={() => navigate(-1)} sx={{ mb: 2 }}>← Back</Button>
      <Card>
        <Grid container sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '45% 55%' }, columnGap: { md: 3 }, alignItems: 'start' }}>
          {/* Image column */}
          <Grid item xs={12} md={5}>
            <Box sx={{ position: 'relative', height: { xs: 'auto', md: 520 }, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {recipe.imageUrl ? (
                <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img
                    src={recipe.imageUrl}
                    alt={recipe.title || 'recipe image'}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      display: 'block'
                    }}
                    onClick={() => setOpenImage(true)}
                  />
                  <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1 }}>
                    <Tooltip title="Fullscreen">
                      <IconButton size="small" onClick={() => setOpenImage(true)} sx={{ bgcolor: 'rgba(255,255,255,0.85)' }}>
                        <FullscreenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download image">
                      <IconButton size="small" component="a" href={recipe.imageUrl} target="_blank" rel="noopener noreferrer" download sx={{ bgcolor: 'rgba(255,255,255,0.85)' }}>
                        <GetAppIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ) : (
                <Typography variant="subtitle2" color="text.secondary">No image</Typography>
              )}
            </Box>
          </Grid>

          {/* Content column */}
          <Grid item xs={12} md={7}>
            <CardContent>
              <Typography variant="h4">{recipe.title || 'Untitled'}</Typography>
              <Typography variant="subtitle2" color="text.secondary">{recipe.sharedByDisplayName || ''}</Typography>
              <Typography sx={{ mt: 2 }}>{recipe.description}</Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6">Ingredients</Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  {(ingredients || []).map((ing, idx) => {
                    return (
                      <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip size="small" label={`${ing.quantity ?? ''} ${ing.unit ?? ''}`.trim() || '—'} variant="filled" color="primary" sx={{ fontWeight: 600 }} />
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{ing.name ?? (typeof ing === 'string' ? ing : '')}</Typography>
                        </Box>
                        {ing.notes && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 5 }}>
                            {ing.notes}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Paper>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6">Steps</Typography>
              <List>
                {steps.map((st, idx) => (
                  <ListItem key={idx} alignItems="flex-start">
                    <Box sx={{ minWidth: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>{st.step_number ?? (idx + 1)}</Box>
                    </Box>
                    <ListItemText primary={st.instruction ?? st} />
                  </ListItem>
                ))}
              </List>

              {recipe.serving_suggestions && recipe.serving_suggestions.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6">Serving Suggestions</Typography>
                  <List dense>
                    {recipe.serving_suggestions.map((s, i) => (
                      <ListItem key={i}><ListItemText primary={s} /></ListItem>
                    ))}
                  </List>
                </>
              )}

              {recipe.notes && recipe.notes.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6">Notes</Typography>
                  <List dense>
                    {recipe.notes.map((n, i) => (
                      <ListItem key={i}><ListItemText primary={n} /></ListItem>
                    ))}
                  </List>
                </>
              )}

              <Divider sx={{ my: 2 }} />
              <Typography variant="caption">Shared at: {formatTimestamp(recipe.sharedAt || recipe.createdAt)}</Typography>

              <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Tooltip title="Share (system)">
                  <IconButton size="small" aria-label="share" onClick={handleWebShare}>
                    <ShareIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Share to Facebook">
                  <IconButton size="small" aria-label="share-facebook" onClick={() => openShareWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`)}>
                    <FacebookIcon color="primary" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Share to X (Twitter)">
                  <IconButton size="small" aria-label="share-twitter" onClick={() => openShareWindow(`https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(title)}`)}>
                    <TwitterIcon sx={{ color: '#1DA1F2' }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Share to Reddit">
                  <IconButton size="small" aria-label="share-reddit" onClick={() => openShareWindow(`https://www.reddit.com/submit?url=${encodeURIComponent(pageUrl)}&title=${encodeURIComponent(title)}`)}>
                    <RedditIcon sx={{ color: '#FF4500' }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Pin to Pinterest">
                  <IconButton size="small" aria-label="share-pinterest" onClick={() => openShareWindow(`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(pageUrl)}&media=${encodeURIComponent(recipe.imageUrl || '')}&description=${encodeURIComponent(title)}`)}>
                    <PinterestIcon sx={{ color: '#E60023' }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Share to LinkedIn">
                  <IconButton size="small" aria-label="share-linkedin" onClick={() => openShareWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`)}>
                    <LinkedInIcon sx={{ color: '#0077B5' }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Share to WhatsApp">
                  <IconButton size="small" aria-label="share-whatsapp" onClick={() => openShareWindow(`https://api.whatsapp.com/send?text=${encodeURIComponent(title + ' ' + pageUrl)}`)}>
                    <WhatsAppIcon sx={{ color: '#25D366' }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Share by Email">
                  <IconButton size="small" aria-label="share-email" onClick={() => { const mail = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent((recipe.description ? recipe.description + '\n\n' : '') + pageUrl)}`; window.location.href = mail; }}>
                    <EmailIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <a
                href="https://apps.apple.com/ca/app/monadx/id6756245698"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download MonadX on the App Store"
                style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, textDecoration: 'none', color: 'inherit' }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, fontStyle: 'italic', display: 'inline-flex' }}>
                  Manage DM campaigns and engage your audience — get the MonadX app.
                </Typography>
                <img
                  src="/monadx-round.png"
                  alt="MonadX App badge"
                  style={{ height: 28, width: 28 }}
                />
              </a>
            </CardContent>
          </Grid>
        </Grid>
      </Card>

      {recipe && (() => {
        const schema = { '@context': 'https://schema.org/', '@type': 'Recipe' };
        if (recipe.title) schema.name = recipe.title;
        if (recipe.description) schema.description = recipe.description;
        const imageUrl = recipe.imageUrl || recipe.image || null;
        if (imageUrl) schema.image = Array.isArray(imageUrl) ? imageUrl : [imageUrl];
        const authorName = recipe.sharedByDisplayName || recipe.author || recipe.sharedByUid || null;
        if (authorName) schema.author = { '@type': 'Person', name: authorName };
        const published = recipe.sharedAt || recipe.createdAt || recipe.publishedAt || null;
        if (published) {
          try { schema.datePublished = new Date(published.seconds ? published.seconds * 1000 : published).toISOString(); } catch (e) {}
        }
        const recipeYield = recipe.servings || recipe.yield || recipe.recipeYield || null;
        if (recipeYield) schema.recipeYield = String(recipeYield);

        if (Array.isArray(recipe.ingredients)) {
          schema.recipeIngredient = recipe.ingredients.map((ing) => {
            if (!ing) return '';
            if (typeof ing === 'string') return ing;
            const qty = (ing.quantity ?? '').toString().trim();
            const unit = (ing.unit ?? '').toString().trim();
            const name = ing.name ?? '';
            const parts = [];
            if (qty) parts.push(qty);
            if (unit) parts.push(unit);
            if (name) parts.push(name);
            return parts.join(' ').trim();
          }).filter(Boolean);
        }

        if (Array.isArray(steps)) {
          schema.recipeInstructions = steps.map((st, idx) => {
            const text = (st && (st.instruction || st.text)) || (typeof st === 'string' ? st : '');
            return { '@type': 'HowToStep', text: text || '', position: st && st.step_number ? st.step_number : idx + 1 };
          }).filter(Boolean);
        }

        // include totalTime if available
        if (recipe.totalTime) schema.totalTime = recipe.totalTime;
        if (recipe.cookTime) schema.cookTime = recipe.cookTime;
        if (recipe.prepTime) schema.prepTime = recipe.prepTime;

        // ensure image is absolute URL if possible
        const ogImage = Array.isArray(schema.image) && schema.image.length ? schema.image[0] : (schema.image || null);

        return (
          <Helmet>
            <title>{recipe.title ? `${recipe.title} — MonadX` : 'MonadX'}</title>
            <meta name="description" content={recipe.description || 'Recipe from MonadX'} />
            <meta property="og:title" content={recipe.title || 'MonadX'} />
            <meta property="og:description" content={recipe.description || ''} />
            {ogImage && <meta property="og:image" content={ogImage} />}
            <meta property="og:type" content="article" />
            <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
            <script type="application/ld+json">{JSON.stringify(schema)}</script>
          </Helmet>
        );
      })()}

      <Dialog
        open={openImage}
        onClose={() => setOpenImage(false)}
        fullWidth
        maxWidth="lg"
        aria-labelledby="recipe-image-dialog"
      >
        <DialogContent sx={{ position: 'relative', bgcolor: 'background.default', p: 0 }}>
          <IconButton
            aria-label="close"
            onClick={() => setOpenImage(false)}
            sx={{ position: 'absolute', right: 8, top: 8, zIndex: 10, bgcolor: 'background.paper' }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
            <img
              src={recipe.imageUrl}
              alt={recipe.title || 'recipe full image'}
              style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', display: 'block' }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default RecipeDetail;
